'use client';
import React, { useRef, useEffect, useState } from 'react';

const successStories = [
  {
    icon: '💔',
    title: 'Healing from Emotional Trauma',
    service: 'Quantum Karmic Healing',
    duration: '3 Sessions',
    story: 'A 35-year-old professional from Mumbai had been struggling with deep emotional pain from a difficult relationship for over 5 years. After 3 sessions of Quantum Karmic Healing, she released the karmic cords, healed her inner child wounds, and found the courage to start a new chapter in her life with joy and confidence.',
    outcome: 'Released emotional blocks, restored self-confidence, found inner peace',
    color: '#9b7fe8',
    initial: 'Client A',
  },
  {
    icon: '😰',
    title: 'Overcoming Chronic Anxiety',
    service: 'Clinical Hypnotherapy',
    duration: '5 Sessions',
    story: 'A 28-year-old software engineer from Bangalore had been suffering from severe anxiety and panic attacks for 3 years. Through Clinical Hypnotherapy, the root cause — a childhood trauma — was identified and healed. Within 5 sessions, the anxiety reduced by 90% and he regained control of his life.',
    outcome: 'Eliminated panic attacks, improved sleep, restored professional performance',
    color: '#c9a84c',
    initial: 'Client B',
  },
  {
    icon: '👨‍👩‍👧',
    title: 'Healing Family Relationships',
    service: 'Family Constellation',
    duration: '2 Sessions',
    story: 'A family from Delhi was experiencing constant conflict and communication breakdown for years. Through Family Constellation sessions, generational trauma patterns were identified and healed. The family dynamics transformed completely, bringing harmony, understanding, and love back into their home.',
    outcome: 'Restored family harmony, improved communication, healed generational patterns',
    color: '#7c5cbf',
    initial: 'Client C',
  },
];

export default function SuccessStoriesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="success-stories"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-30 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 glow-orb-gold" style={{ width: '400px', height: '400px', opacity: 0.25 }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
              <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Real Journeys</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Success Stories
            </h2>
            <div className="section-divider mb-4" />
            <p className="text-[#c4b5fd]/65 max-w-2xl mx-auto text-sm leading-relaxed">
              Real client journeys shared with full confidentiality. Names and identifying details have been changed to protect privacy.
            </p>
          </div>

          {/* Stories */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {successStories?.map((story, index) => (
              <div
                key={story?.title}
                className="rounded-2xl p-6 flex flex-col"
                style={{
                  background: 'rgba(22, 12, 40, 0.80)',
                  border: '1px solid rgba(201,168,76,0.22)',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.7s ease ${index * 150}ms, transform 0.7s ease ${index * 150}ms`,
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
                    style={{ background: `${story?.color}20`, border: `1px solid ${story?.color}40` }}
                  >
                    {story?.icon}
                  </div>
                  <div>
                    <h3 className="font-serif text-base font-semibold text-[#e8dff5]">{story?.title}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: `${story?.color}15`, color: story?.color, border: `1px solid ${story?.color}30` }}
                      >
                        {story?.service}
                      </span>
                      <span className="text-[#c4b5fd]/40 text-xs">{story?.duration}</span>
                    </div>
                  </div>
                </div>

                <p className="text-[#c4b5fd]/70 text-sm leading-relaxed mb-4 flex-1">
                  {story?.story}
                </p>

                <div
                  className="rounded-xl p-3 mt-auto"
                  style={{
                    background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, rgba(124,92,191,0.08) 100%)',
                    border: '1px solid rgba(201,168,76,0.20)',
                  }}
                >
                  <div className="text-[#c9a84c] text-xs font-medium mb-1">✨ Outcome</div>
                  <p className="text-[#e8dff5]/75 text-xs leading-relaxed">{story?.outcome}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-[#c4b5fd]/50 text-sm mb-4">Ready to write your own success story?</p>
            <a
              href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-block"
            >
              Begin Your Healing Journey
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
